import { useEffect, useMemo, useState } from 'react'
import { format, isAfter, isBefore } from 'date-fns'
import PropTypes from 'prop-types'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import clsx from 'clsx'
import {
  nullablePropType,
  shippingSend1FromPropTypes,
} from '../../../common/propTypes'
import {
  makeFieldErrorsPropType,
  makeValuesPropType,
} from '../../../common/formikPropTypes'
import useTranslations from '../../../i18n/useTranslations'
import { useUserContext } from '../../../auth/UserProvider'
import useFixedPickup, { parseCentersShippingAddress } from './useFixedPickup'
import ActionButtons from '../../../ui/views/shippingSend/ActionButtons'
import ShippingSendForm from '../../../ui/views/shippingSend/FormWrapper'
import { STEPS, ADDRESS_SOURCES } from '../../../common/constants'
import LinkButtonTooltip from '../../../ui/components/LinkButtonTooltip'

import useStyles from './styles'
import AddressBook from '../AddressBook'
import useDebounce from '../useDebounce'
import calculateProvinceAndLocalities, {
  GEOCODING_ERRORS,
} from '../calculateProvinceAndLocalities'
import countryTelephoneData from 'country-telephone-data'
import Sender from './Sender'
import NewAddress from './NewAddress'
import Addresses from './Addresses'
import SourceAddressSelector from './SourceAddressSelector'
import Step1OriginView from './View'
import ChoosePickup from './Pickup/ChoosePickup'
import Asap from './Pickup/Asap'
import Pickup, { PICKUP_OPTIONS } from './Pickup'
import useAvailableHours from './useAvailableHours'
import { useOriginCountries } from '../../../services/originCountries'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { Box, Button } from '@mui/material'
import Radio from '../../../ui/components/radio'
import { ReactComponent as Delete } from '../../../assets/icons/trash.svg'
import ModalLayout from '../../../ui/modals/layout'
import OriginPudo from './OriginPudo'

export const DEBOUNCED_POSTAL_CODE_DELAY = 1000

export const SCHEDULE_OPTIONS = {
  anytime: 'anytime',
  chooseTimezone: 'chooseTimezone',
}

export const calculateSchedule = (day, daySchedules) => {
  const schedules = daySchedules.find(
    schedule =>
      format(day, 'dd/MM/yyyy') === format(schedule[0].minDate, 'dd/MM/yyyy'),
  )

  const cutoffDate = new Date(day)
  cutoffDate.setHours(15)
  cutoffDate.setMinutes(0)
  cutoffDate.setSeconds(0)

  const morningSchedules = schedules
    ? schedules.filter(daySchedule => isBefore(daySchedule.maxDate, cutoffDate))
        ?.length
      ? schedules.filter(daySchedule =>
          isBefore(daySchedule.maxDate, cutoffDate),
        )
      : schedules.filter(daySchedule =>
          isBefore(daySchedule.minDate, cutoffDate),
        )
    : []

  const afternoonSchedules = schedules
    ? schedules.filter(daySchedule => isAfter(daySchedule.maxDate, cutoffDate))
    : []

  const availableSchedules = {}
  const selectedDay = format(cutoffDate, 'yyyy/MM/dd')

  // NOTE por decisión del cliente, los horarios serán siempre enviados con los
  // mismos intervalos
  const MORNING_START = '09:00'
  const MORNING_END = '14:00'
  const AFTERNOON_START = '15:00'
  const AFTERNOON_END = '19:30'

  if (morningSchedules.length > 0) {
    availableSchedules.morning = {
      minDate: new Date(`${selectedDay} ${MORNING_START}`),
      maxDate: new Date(`${selectedDay} ${MORNING_END}`),
    }
  }

  if (afternoonSchedules.length > 0) {
    availableSchedules.afternoon = {
      minDate: new Date(`${selectedDay} ${AFTERNOON_START}`),
      maxDate: new Date(`${selectedDay} ${AFTERNOON_END}`),
    }
  }

  return availableSchedules
}

const createPickupDateId = ({ minDate, maxDate }) => {
  return `${minDate}/${maxDate}`
}

export const calculateScheduleOptions = schedules => {
  const options = []

  if (Object.keys(schedules).length === 0) {
    return [
      {
        i18nKey: 'message:no-results',
        value: 'no-options',
        disabled: false,
      },
    ]
  }

  if (schedules.morning) {
    options.push({
      i18nKey: 'shipping-send-form:origin:timetable:morning',
      i18nOptions: schedules.morning,
      value: createPickupDateId(schedules.morning),
      // value: schedules.morning,
      disabled: false,
    })
  }

  if (schedules.afternoon) {
    options.push({
      i18nKey: 'shipping-send-form:origin:timetable:afternoon',
      i18nOptions: schedules.afternoon,
      value: createPickupDateId(schedules.afternoon),
      disabled: false,
    })
  }

  if (Object.keys(schedules).length !== 1) {
    options.push({
      i18nKey: 'shipping-send-form:origin:timetable:anytime',
      i18nOptions: undefined,
      value: createPickupDateId({
        minDate: schedules.morning.minDate,
        maxDate: schedules.afternoon.maxDate,
      }),
      disabled: false,
    })
  }

  options.push({
    i18nKey: 'shipping-send-form:origin:timetable:select-timeslot',
    i18nOptions: undefined,
    value: SCHEDULE_OPTIONS.chooseTimezone,
    disabled: false,
  })

  return options
}

export const calculateTimezoneOptionsByDay = (day, timezones) => {
  const timezonesByDay = timezones.find(timezone => {
    const timezoneDate = format(timezone[0].minDate, 'yyyy/MM/dd')
    return format(day, 'yyyy/MM/dd') === timezoneDate
  })

  return timezonesByDay ? timezonesByDay.map(createPickupDateId) : undefined
}

const POSTAL_CODES_LENGTH = {
  es: 5,
  pt: 7,
}

const getOriginSchema = () => {
  return Yup.object().shape({
    imputationCenter: Yup.object().shape({
      id: Yup.string(),
      name: Yup.string(),
    }),
    addressSource: Yup.string(),
    selectedAddress: Yup.object(),
    address: Yup.object()
      .when('addressSource', {
        is: ADDRESS_SOURCES.newAddress,
        then: Yup.object().shape({
          country: Yup.object().shape({
            label: Yup.string().required(),
            code: Yup.string().required(),
          }),
          contactClientCode: Yup.string(),
          address: Yup.string().required(),
          name: Yup.string().required(),
          phonePrefix: Yup.string(),
          phone: Yup.string()
            .test(
              'isValidPhoneNumber',
              {
                key: 'shipping-send-form:new-address:phone-format-or-length-error',
              },
              function (value) {
                if (!value) return true
                const regex = /^\d+$/
                return regex.test(value.replace(/\s+/g, ''))
              },
            ),
          /*.required()
          .test(
            'isValidPhoneNumber',
            {
              key: 'shipping-send-form:new-address:phone-format-or-length-error',
            },
            function (value, schema) {
              const phonePrefix = schema.parent.phonePrefix
              const fullPhoneNumber = `${phonePrefix}${value}`
              return isValidPhoneNumber(fullPhoneNumber)
            },
          )*/ email: Yup.string().email(),
          stair: Yup.string(),
          floor: Yup.string(),
          letter: Yup.string(),
          postalCode: Yup.string().when('country.code', {
            is: 'ES',
            then: Yup.string()
              .length(POSTAL_CODES_LENGTH.es, {
                key: 'shipping-send-form:origin:postal-code:invalid-format',
              })
              .required({
                key: 'error:field:postal-code:required',
              }),
            otherwise: Yup.string().when('country.code', {
              is: 'PT',
              then: Yup.string()
                .matches(/^(?:\d{4}|\d{4}-\d{3})$/, {
                  key: 'shipping-send-form:origin:postal-code:invalid-format',
                })
                .required({
                  key: 'error:field:postal-code:required',
                }),
              otherwise: Yup.string().required({
                key: 'error:field:postal-code:required',
              }),
            }),
          }),
          province: Yup.string(),
          city: Yup.string().required(),
        }),
      })
      .when('addressSource', {
        is: ADDRESS_SOURCES.pickUpPoint,
        then: Yup.object().shape({
          phonePrefix: Yup.string(),
          phone: Yup.string()
            .nullable()
            .test(
              'isValidPhoneNumber',
              {
                key: 'shipping-send-form:new-address:phone-format-or-length-error',
              },
              function (value) {
                if (!value) return true
                const regex = /^\d+$/
                return regex.test(value.replace(/\s+/g, ''))
              },
            ),
          /*.test(
            'isValidPhoneNumber',
            {
              key: 'shipping-send-form:new-address:phone-format-or-length-error',
            },
            function (value, schema) {
              if (!value) return true;
              const phonePrefix = schema.parent.phonePrefix
              const fullPhoneNumber = `${phonePrefix}${value}`
              return isValidPhoneNumber(fullPhoneNumber)
            },
          )*/ email: Yup.string().email(),
        }),
      }),
    addressBook: Yup.object().nullable(),
    newAddress: Yup.object().nullable(),
    pickupDay: Yup.string(),
    pickupSchedule: Yup.string(),
    pickupTimezone: Yup.string(),
    pickupSource: Yup.string(),
    pickup: Yup.string().nullable(),
    comments: Yup.string(),
  })
}

// const COUNTRIES = [
//   {
//     code: 'ES',
//     label: 'España',
//   },
//   {
//     code: 'PT',
//     label: 'Portugal',
//   },
// ];

const DEFAULT_PHONE_PREFIX = '+34'

const Step1Origin = ({
  savedValues,
  activeStep,
  serverFieldErrors,
  hasOtherError,
  onSubmitSuccess,
  onCancel,
}) => {
  const formatMessage = useTranslations()
  const { data: countries } = useOriginCountries()

  const classes = useStyles()
  const { centres, clientCode } = useUserContext()
  parseCentersShippingAddress(centres)
  const defaultCenter = centres[0]

  const [deleteCreatedAddress, setDeleteCreatedAddress] = useState()
  const [isAddressBookOpen, setIsAddressBookOpen] = useState(false)
  const [fromSearch, setFromSearch] = useState(false)
  const closeAddressBook = () => {
    setIsAddressBookOpen(false)
  }

  const getPhoneInfo = phoneSource => {
    let phone = phoneSource
    let prefix = '+34'

    if (phone?.length && phone[0] === '+') {
      let findPrefix = countryTelephoneData.allCountries.filter(
        ({ dialCode }) => phone.startsWith('+' + dialCode),
      )

      phone = phone.replace(
        '+' + findPrefix[findPrefix?.length - 1]?.dialCode,
        '',
      )
      prefix = '+' + findPrefix[findPrefix?.length - 1]?.dialCode
    }
    return { prefix: prefix, phone: phone }
  }

  // NOTE: no me termina de convencer esta implementación para saber si la
  // provincia y la localidad son editables.
  // REVIEW: si cabe la posibilidad de dedecarle tiempo a esta funcionalidad,
  // buscar la manera de no hacerlo tan rudimentario. Lo ideal sería que estuviese
  // dentro del propio formulario `NewAddress/index.jsx` (este es otro refactor
  // que habría que abordar).

  const [initialLoad, setInitialLoad] = useState(true)
  const [zeroResultsGeocode, setZeroResultsGeocode] = useState(false)

  const formik = useFormik({
    validateOnChange: false,
    validationSchema: getOriginSchema(),
    initialValues:
      savedValues !== null
        ? savedValues
        : {
            imputationCenter: defaultCenter,
            selectedAddress: defaultCenter,
            addressSource: ADDRESS_SOURCES.clientCentersList,
            addressBook: null,
            newAddress: null,
            address: {
              country: { code: '', label: '' },
              name: '',
              address: '',
              phone: '',
              phonePrefix: DEFAULT_PHONE_PREFIX,
              email: '',
              stair: '',
              floor: '',
              letter: '',
              postalCode: '',
              province: '',
              city: '',
              hasSeveralLocalities: false,
              localitiesOptions: null,
              contactClientCode: '',
            },
            pickupDay: '',
            pickupSchedule: 'none',
            pickupTimezone: 'none',
            pickupSource:
              sessionStorage.getItem('pickupSource') || PICKUP_OPTIONS.asap,
            pickup: null,
            fixedPickup: false,
            comments: '',
          },
    onSubmit: values => {
      handleOnSubmitSuccess(values)
    },
  })
  const debouncedPostalCode = useDebounce(
    formik.values.address?.postalCode,
    DEBOUNCED_POSTAL_CODE_DELAY,
  )

  const handleOnSubmitSuccess = values => {
    const isOriginPudo = values.addressSource === ADDRESS_SOURCES.pickUpPoint
    onSubmitSuccess(values, isOriginPudo)
  }

  const availableHours = useAvailableHours(
    formik.values.addressSource === ADDRESS_SOURCES.clientCentersList
      ? formik.values.selectedAddress.shippingAddress.postalCode
      : debouncedPostalCode,
    format(new Date(), 'dd-MM-yyyy'),
  )

  const enabledCalendarDays = useMemo(
    () =>
      !Array.isArray(availableHours.data)
        ? []
        : availableHours.data.map(pickupDay =>
            format(pickupDay[0].minDate, 'yyyy-MM-dd'),
          ),
    [availableHours.data],
  )

  const schedules =
    availableHours?.data && formik.values.pickupDay !== ''
      ? calculateSchedule(formik.values.pickupDay, availableHours.data)
      : []
  const scheduleOptions = calculateScheduleOptions(schedules)

  const timezoneOptions =
    Array.isArray(availableHours.data) && formik.values.pickupDay !== ''
      ? calculateTimezoneOptionsByDay(
          formik.values.pickupDay,
          availableHours.data,
        )
      : []

  // check if current address matchs with fixed pickups addresses
  const currentAddress =
    formik.values.addressSource === ADDRESS_SOURCES.clientCentersList
      ? formik.values.selectedAddress?.shippingAddress
      : {
          address: formik.values.address?.address,
          city: formik.values.address?.city,
          country: formik.values.address?.country?.code,
          postalCode: formik.values.address?.postalCode,
        }
  const { firstFixedPickupTimeZone } = useFixedPickup(
    centres,
    formik.values.imputationCenter.id,
    currentAddress,
  )

  const [firstPickupDate, setFirstPickupDate] = useState(null)

  useEffect(() => {
    const setFixedPickupDates = () => {
      const pickupDateId = createPickupDateId({
        minDate: firstFixedPickupTimeZone.fromDate,
        maxDate: firstFixedPickupTimeZone.toDate,
      })
      formik.setFieldValue('pickup', pickupDateId)
      setFirstPickupDate(pickupDateId)
    }

    const setAsapDates = () => {
      const firstPickupDay = availableHours?.data?.at(0)
      const minDateTimeSlot = firstPickupDay?.at(0).minDate
      const maxDateTimeSlot = firstPickupDay?.at(
        firstPickupDay?.length - 1,
      ).maxDate
      const pickupDateId = createPickupDateId({
        minDate: minDateTimeSlot,
        maxDate: maxDateTimeSlot,
      })
      formik.setFieldValue('pickup', pickupDateId)
      setFirstPickupDate(pickupDateId)
    }

    const setFixedPickup = () => {
      formik.setFieldValue(
        'fixedPickup',
        (formik.values.pickupSource === PICKUP_OPTIONS.asap ||
          formik.values.pickupSource === PICKUP_OPTIONS.fixedPickUp) &&
          !!firstFixedPickupTimeZone,
      )
    }

    const setRequestLaterPickup = () => {
      formik.setFieldValue('pickup', null)
    }

    if (
      (formik.values.pickupSource === PICKUP_OPTIONS.asap ||
        formik.values.pickupSource === PICKUP_OPTIONS.fixedPickUp) &&
      availableHours.isFetched &&
      !availableHours.isError
    ) {
      if (firstFixedPickupTimeZone) {
        setFixedPickupDates()
      } else {
        setAsapDates()
      }
      setFixedPickup()
    } else if (
      formik.values.pickupSource === PICKUP_OPTIONS.requestLater &&
      firstFixedPickupTimeZone !== undefined
    ) {
      formik.setFieldValue('pickupSource', PICKUP_OPTIONS.asap)
    } else if (formik.values.pickupSource === PICKUP_OPTIONS.requestLater) {
      setRequestLaterPickup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    availableHours.isFetched,
    firstFixedPickupTimeZone,
    formik.values.pickupSource,
  ])

  const changePickupDate = pickup => {
    formik.setFieldValue('pickup', pickup)
  }

  useEffect(() => {
    if (
      formik.values?.addressSource === ADDRESS_SOURCES.newAddress &&
      !isAddressBookOpen &&
      formik.values?.addressBook
    )
      formik.setValues(values => ({
        ...values,
        address: formik.values?.addressBook?.shippingAddress,
      }))
  }, [isAddressBookOpen])

  useEffect(() => {
    sessionStorage.setItem('pickupSource', formik.values.pickupSource)
  }, [formik.values.pickupSource])

  useEffect(() => {
    if (
      !initialLoad &&
      debouncedPostalCode.trim() !== '' &&
      formik.values.address?.country?.code !== '' &&
      formik.values.address?.country?.code !== undefined &&
      debouncedPostalCode === formik.values?.address?.postalCode &&
      formik?.values?.addressBook?.shippingAddress?.postalCode !==
        debouncedPostalCode
    ) {
      calculateProvinceAndLocalities(
        debouncedPostalCode,
        formik.values.address?.country?.code,
        fromSearch,
      )
        .then(result => {
          formik.setValues(values => ({
            ...values,
            address: {
              ...values.address,
              ...result,
              city: result?.city?.length
                ? result?.city
                : values.address?.city ?? '',
              province: result?.province?.length
                ? result?.province
                : values.address?.province ?? '',
            },
          }))

          setZeroResultsGeocode(false)

          const errors = { ...formik.errors }

          if (formik.errors.address?.postalCode)
            delete errors.address?.postalCode

          if (formik.errors.address?.city) delete errors.address.city

          if (formik.errors.address?.province) delete errors.address.province

          formik.setErrors(errors)
        })
        .catch(error => {
          formik.setValues(values => ({
            ...values,
            address: {
              ...values.address,
              city: values.address?.city ?? '',
              province: values.address?.province ?? '',
              hasSeveralLocalities: false,
              localitiesOptions: null,
            },
          }))

          if (error.code === GEOCODING_ERRORS.zeroResults) {
            setZeroResultsGeocode(true)
          }
        })
    } else {
      setInitialLoad(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPostalCode, formik.values.address?.country, fromSearch])

  const renderDeleteCreatedAddress = () => {
    return (
      <ModalLayout
        isOpen={deleteCreatedAddress}
        title={formatMessage('action:delete-created-address-header')}
        content={formatMessage('action:delete-created-address-body')}
        actions={
          <>
            <Button
              type="button"
              size="small"
              variant="outlined"
              onClick={() => {
                formik.setValues(values => ({
                  ...values,
                  address: values?.newAddress?.shippingAddress,
                  addressBook: null,
                }))
                setDeleteCreatedAddress(null)
              }}
            >
              {formatMessage('action:cancel')}
            </Button>
            <Button
              size="small"
              onClick={() => {
                formik.setValues(values => ({
                  ...values,
                  newAddress: null,
                  address: values?.addressBook?.shippingAddress ?? {
                    email: '',
                    name: '',
                    phone: '',
                    postalCode: '',
                    phonePrefix: '+34',
                    floor: '',
                    letter: '',
                    stair: '',
                    country: { code: 'ES', label: '' },
                    address: '',
                    province: '',
                    city: '',
                    hasSeveralLocalities: false,
                    localitiesOptions: null,
                  },
                }))
                setDeleteCreatedAddress(null)
              }}
            >
              {formatMessage('action:confirm')}
            </Button>
          </>
        }
      />
    )
  }

  const formatAddress = details => {
    let { address, stair, floor, letter } = details

    if (stair) {
      address += `, escalera ${stair}`
    }

    if (floor) {
      address += `, piso ${floor}`
    }

    if (letter) {
      address += `, letra ${letter}`
    }

    return address
  }

  return (
    <ShippingSendForm onSubmit={formik.handleSubmit}>
      {renderDeleteCreatedAddress()}
      <Step1OriginView>
        <Sender
          imputationCenters={centres}
          selectedImputationCenter={formik.values.imputationCenter}
          onBlur={() => {}}
          onChange={e => {
            if (
              formik.values.addressSource === ADDRESS_SOURCES.clientCentersList
            ) {
              const address = e.target.value
              formik.setValues(values => ({
                ...values,
                selectedAddress: address,
                addressBook: null,
                newAddress: null,
              }))
              formik.handleChange(e)
            }
          }}
        />
        <SourceAddressSelector
          addressSource={formik.values.addressSource}
          changeAddressSource={addressSource => {
            if (addressSource === ADDRESS_SOURCES.clientCentersList) {
              formik.setValues(values => ({
                ...values,
                addressSource,
                address: {
                  address:
                    formik.values.selectedAddress.shippingAddress.address,
                  name: formik.values.selectedAddress.shippingAddress.name,
                  phone: formik.values.selectedAddress.shippingAddress.phone,
                  phonePrefix: DEFAULT_PHONE_PREFIX,
                  email: formik.values.selectedAddress.shippingAddress.email,
                  postalCode:
                    formik.values.selectedAddress.shippingAddress.postalCode,
                  stair: '',
                  floor: '',
                  letter: '',
                  hasSeveralLocalities: false,
                  localitiesOptions: null,
                },
              }))
            } else if (addressSource === ADDRESS_SOURCES.newAddress) {
              formik.setErrors({})
              if (formik.values?.newAddress) {
                formik.setValues(values => ({
                  ...values,
                  addressSource,
                  address: values?.newAddress?.shippingAddress,
                }))
              } else if (formik.values?.addressBook) {
                formik.setValues(values => ({
                  ...values,
                  addressSource,
                  address: values?.addressBook?.shippingAddress,
                }))
              } else {
                formik.setValues(values => ({
                  ...values,
                  addressSource,
                  address: {
                    country: { code: 'ES', label: 'España' },
                    address: '',
                    name: '',
                    phone: '',
                    phonePrefix: DEFAULT_PHONE_PREFIX,
                    email: '',
                    stair: '',
                    floor: '',
                    letter: '',
                    postalCode: '',
                    province: '',
                    city: '',
                    hasSeveralLocalities: false,
                    localitiesOptions: null,
                  },
                }))
              }
            } else if (addressSource === ADDRESS_SOURCES.pickUpPoint) {
              formik.setErrors({})
              formik.setValues(values => ({
                ...values,
                addressSource,
                address: {
                  country: { code: 'ES', label: 'España' },
                  address: 'AVENIDA EUROPA',
                  name: '9',
                  phone: '',
                  phonePrefix: DEFAULT_PHONE_PREFIX,
                  email: '',
                  stair: '',
                  floor: '',
                  letter: '',
                  postalCode: '28821',
                  province: 'Madrid',
                  city: 'Coslada',
                  hasSeveralLocalities: false,
                  localitiesOptions: null,
                },
              }))
            }
          }}
        >
          {formik.values.addressSource === ADDRESS_SOURCES.clientCentersList ? (
            <>
              <Addresses
                addresses={centres
                  .filter(
                    address => address.id === formik.values.imputationCenter.id,
                  )
                  .filter(x => x !== null)}
                selectedAddressId={formik.values.selectedAddress.id}
                selectAddress={center => {
                  if (
                    formik.values.addressSource ===
                    ADDRESS_SOURCES.clientCentersList
                  ) {
                    formik.setValues(values => ({
                      ...values,
                      selectedAddress: center,
                      newAddress: null,
                      address: {
                        address: center.shippingAddress.address,
                        email: center.shippingAddress.email,
                        name: center.shippingAddress.name,
                        phone: center.shippingAddress.phone,
                        phonePrefix: DEFAULT_PHONE_PREFIX,
                        postalCode: center.shippingAddress.postalCode,
                        country: {
                          code: center.shippingAddress?.country,
                          label: '',
                        },
                        floor: '',
                        letter: '',
                        stair: '',
                        hasSeveralLocalities: false,
                        localitiesOptions: null,
                      },
                    }))
                  }
                }}
              />
            </>
          ) : null}

          {formik.values.addressSource === ADDRESS_SOURCES.newAddress ? (
            <>
              {formik.values.newAddress ? (
                <Box className={classes.radiosContainer}>
                  <Box
                    key={formik.values.newAddress.id}
                    className={clsx(classes.radioWrapper, {
                      'is-checked': true,
                    })}
                  >
                    <Radio
                      className={classes.radio}
                      label={formik.values.newAddress.name}
                      subLabel={formatMessage('address:without-name', {
                        address:
                          formik.values.newAddress.shippingAddress.address,
                        postalCode:
                          formik.values.newAddress.shippingAddress.postalCode,
                        city: formik.values.newAddress.shippingAddress.city,
                        country:
                          formik.values.newAddress.shippingAddress.country
                            ?.code ??
                          formik.values.newAddress.shippingAddress.country,
                      })}
                      additionalText={
                        formik.values.newAddress.shippingAddress.email ||
                        formik.values.newAddress.shippingAddress.phone
                          ? `${
                              formik.values.newAddress.shippingAddress.email ||
                              ''
                            }${
                              formik.values.newAddress.shippingAddress.email &&
                              formik.values.newAddress.shippingAddress.phone
                                ? ', '
                                : ''
                            }${
                              formik.values.newAddress.shippingAddress.phone ||
                              ''
                            }`
                          : null
                      }
                      multiline={false}
                      checked={true}
                    />
                    <Delete
                      className={classes.deleteIcon}
                      onClick={() => setDeleteCreatedAddress(true)}
                    />
                  </Box>
                </Box>
              ) : null}
              <Box className={classes.newAddressButton}>
                <LinkButtonTooltip
                  text={formatMessage('action:new-address')}
                  onClick={() => {
                    formik.setValues(values => ({
                      ...values,
                      address: {
                        email: '',
                        name: '',
                        phone: '',
                        postalCode: '',
                        phonePrefix: DEFAULT_PHONE_PREFIX,
                        floor: '',
                        letter: '',
                        stair: '',
                        country: { code: 'ES', label: '' },
                        address: '',
                        province: '',
                        city: '',
                        hasSeveralLocalities: false,
                        localitiesOptions: null,
                      },
                    }))
                    setIsAddressBookOpen(true)
                  }}
                />
              </Box>
              <AddressBook
                currentStep={STEPS.FROM}
                centerCode={formik.values.imputationCenter.id}
                currentAddressBook={
                  formik.values?.addressBook?.shippingAddress ?? true
                }
                selectDirection={direction => {
                  // REVIEW: considerar vaciar `selectedAddress` y guardar
                  // únicamente en `addressBook`. En otras partes del código
                  // se podría saber de dónde sacar la información en base a
                  // la propiedad `addressSource`.
                  formik.setValues(values => ({
                    ...values,
                    //selectedAddress: null,
                    address: {
                      email: direction?.email ?? '',
                      name: direction?.name ?? '',
                      phone: getPhoneInfo(direction?.phone)?.phone ?? '',
                      postalCode: direction?.postalCode ?? '',
                      phonePrefix: getPhoneInfo(direction?.phone)?.prefix,
                      floor: '',
                      letter: '',
                      stair: '',
                      country: {
                        code: direction?.country,
                        label: countries.find(
                          ({ country_code }) =>
                            country_code === direction?.country,
                        )?.country_name,
                      },
                      address: direction?.address ?? '',
                      province: direction?.province ?? '',
                      city: direction?.city ?? '',
                      hasSeveralLocalities: false,
                      localitiesOptions: null,
                    },
                    addressBook: {
                      id: direction.id,
                      name: direction.name,
                      isAddressBook: true,
                      shippingAddress: {
                        ...direction,
                      },
                    },
                  }))
                  if (formik?.values?.newAddress) setDeleteCreatedAddress(true)
                }}
                onBlur={e => {
                  formik.validateField(e.target.name)
                }}
                setErrors={formik.setErrors}
                isPhoneRequired
              />
              {isAddressBookOpen ? (
                <NewAddress
                  countries={countries}
                  isOpen={isAddressBookOpen}
                  close={closeAddressBook}
                  clientCode={clientCode}
                  centerCode={formik.values.imputationCenter.id}
                  zeroResultsGeocode={zeroResultsGeocode}
                  values={formik.values}
                  errors={formik.errors}
                  onSubmit={direction => {
                    // REVIEW: considerar vaciar `selectedAddress` y guardar
                    // únicamente en `addressBook`. En otras partes del código
                    // se podría saber de dónde sacar la información en base a
                    // la propiedad `addressSource`.
                    direction.address = direction?.address
                      ? formatAddress(direction)
                      : ''
                    formik.setValues(values => ({
                      ...values,
                      //selectedAddress: null,
                      address: {
                        email: direction?.email ?? '',
                        name: direction?.name ?? '',
                        phone: getPhoneInfo(direction?.phone)?.phone ?? '',
                        postalCode: direction?.postalCode ?? '',
                        phonePrefix: getPhoneInfo(direction?.phone)?.prefix,
                        floor: '',
                        letter: '',
                        stair: '',
                        country: {
                          code: direction?.country?.code,
                          label: countries.find(
                            ({ country_code }) =>
                              country_code === direction?.country?.code,
                          )?.country_name,
                        },
                        address: direction?.address ?? '',
                        province: direction?.province ?? '',
                        city: direction?.city ?? '',
                        hasSeveralLocalities: false,
                        localitiesOptions: null,
                      },
                      addressBook: null,
                      newAddress: {
                        id: direction.id,
                        name: direction.name,
                        shippingAddress: {
                          ...direction,
                        },
                      },
                    }))
                  }}
                  autofillAddress={addressInfo => {
                    setFromSearch(addressInfo?.place)
                    formik.setValues(values => ({
                      ...values,
                      address: {
                        ...values.address,
                        ...addressInfo,
                      },
                    }))
                  }}
                  onChange={e => {
                    setFromSearch(false)
                    formik.handleChange(e)
                  }}
                  onBlur={e => {
                    formik.validateField(e.target.name)
                  }}
                  validate={formik.validateForm}
                  setErrors={formik.setErrors}
                  isPhoneRequired
                />
              ) : null}
            </>
          ) : null}

          {formik.values.addressSource === ADDRESS_SOURCES.pickUpPoint && (
            <OriginPudo
              values={formik.values}
              errors={formik.errors}
              handleChange={formik.handleChange}
              handleBlur={formik.handleBlur}
            />
          )}
        </SourceAddressSelector>
        {formik.values.addressSource !== ADDRESS_SOURCES.pickUpPoint && (
          <Pickup
            changePickupSource={pickupSource => {
              formik.setValues(values => ({
                ...values,
                pickupSource,
              }))
            }}
            pickup={formik.values.pickup}
            pickupSource={formik.values.pickupSource}
            isChoosePickupDateDisabled={
              availableHours.isLoading ||
              availableHours.isError ||
              !availableHours?.data?.length
            }
            postalCode={
              formik.values.addressSource === ADDRESS_SOURCES.clientCentersList
                ? formik.values.selectedAddress?.shippingAddress?.postalCode
                : formik.values.address?.postalCode
            }
            fixedPickupTimeZone={firstFixedPickupTimeZone}
          >
            {formik.values.pickupSource === PICKUP_OPTIONS.asap &&
              !firstFixedPickupTimeZone && (
                <Asap
                  isError={
                    availableHours.isError || !availableHours?.data?.length
                  }
                  isLoading={availableHours.isLoading}
                  pickupDate={firstPickupDate}
                  errorMessage={
                    !availableHours?.data?.length
                      ? formatMessage(
                          'shipping-send-form:origin:not-available-dates',
                        )
                      : null
                  }
                />
              )}

            {formik.values.pickupSource === PICKUP_OPTIONS.fixedPickUp &&
              !!firstFixedPickupTimeZone && (
                <Asap pickupDate={firstPickupDate} />
              )}

            {formik.values.pickupSource === PICKUP_OPTIONS.chooseDayAndHour && (
              <ChoosePickup
                changeDay={day => {
                  formik.setFieldValue('pickupDay', day)
                }}
                changeSchedule={schedule => {
                  formik.setFieldValue('pickupSchedule', schedule)
                }}
                changeTimezone={timezone => {
                  formik.setFieldValue('pickupTimezone', timezone)
                }}
                enabledCalendarDays={enabledCalendarDays}
                isLoading={availableHours.isLoading}
                scheduleOptions={scheduleOptions}
                timezoneOptions={timezoneOptions}
                selectedSchedule={formik.values.pickupSchedule}
                selectedDay={formik.values.pickupDay}
                selectedTimezone={formik.values.pickupTimezone}
                pickupDate={formik.values.pickup}
                changePickupDate={changePickupDate}
                firstPickupDate={firstPickupDate}
                hasFixedPickup={!!firstFixedPickupTimeZone}
              />
            )}
          </Pickup>
        )}
      </Step1OriginView>
      <ActionButtons
        isDirty={formik.dirty}
        isPrimaryDisabled={
          (formik.values.addressSource !== ADDRESS_SOURCES.pickUpPoint &&
            (availableHours.isError || !availableHours?.data?.length)) ||
          (formik.values.addressSource === ADDRESS_SOURCES.pickUpPoint &&
            (!formik.values.address.email ||
              Object.keys(formik.errors).length > 0))
        }
        isSaved={savedValues !== null}
        onCancel={onCancel}
        hasOtherError={hasOtherError}
        hasServerError={
          !!serverFieldErrors && Object.keys(serverFieldErrors).length > 0
        }
        currentStep={STEPS.FROM}
        activeStep={activeStep}
      />
    </ShippingSendForm>
  )
}

Step1Origin.propTypes = {
  savedValues: nullablePropType(
    makeValuesPropType(shippingSend1FromPropTypes).isRequired,
  ),
  activeStep: PropTypes.oneOf(Object.values(STEPS)).isRequired,
  serverFieldErrors: nullablePropType(
    makeFieldErrorsPropType(shippingSend1FromPropTypes).isRequired,
  ),
  hasOtherError: PropTypes.bool.isRequired,
  onSubmitSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}

export default Step1Origin
